import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { orderStatusesAPI } from '../services/api'

export const useOrderStatusesStore = defineStore('orderStatuses', () => {
  // State
  const statuses = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const activeStatuses = computed(() => 
    statuses.value.filter(status => status.is_active)
  )

  const statusesMap = computed(() => {
    const map = {}
    activeStatuses.value.forEach(status => {
      map[status.name] = status
    })
    return map
  })

  const getStatusDisplayName = (statusName) => {
    const status = statusesMap.value[statusName]
    return status ? status.display_name : statusName
  }

  const getStatusColorClass = (statusName) => {
    const status = statusesMap.value[statusName]
    return status ? status.color_class : 'bg-gray-100 text-gray-800'
  }

  const getStatusDescription = (statusName) => {
    const status = statusesMap.value[statusName]
    return status ? status.description : ''
  }

  // Actions
  const fetchStatuses = async () => {
    try {
      loading.value = true
      error.value = null
      
      const response = await orderStatusesAPI.getStatuses()
      statuses.value = response.statuses || []
    } catch (err) {
      console.error('Failed to fetch order statuses:', err)
      error.value = err.response?.data?.message || 'Failed to fetch order statuses'
      
      // Fallback to default statuses if API fails
      statuses.value = [
        {
          id: 1,
          name: 'pending',
          display_name: 'Pending',
          description: 'Order is pending approval',
          color_class: 'bg-yellow-100 text-yellow-800',
          is_active: true,
          sort_order: 1
        },
        {
          id: 2,
          name: 'advance_paid',
          display_name: 'Advance Paid',
          description: 'Advance payment received',
          color_class: 'bg-blue-100 text-blue-800',
          is_active: true,
          sort_order: 2
        },
        {
          id: 3,
          name: 'pending_in_workshop',
          display_name: 'Pending in Workshop',
          description: 'Order assigned to workshop but not started',
          color_class: 'bg-purple-100 text-purple-800',
          is_active: true,
          sort_order: 3
        },
        {
          id: 4,
          name: 'in_workshop',
          display_name: 'In Workshop',
          description: 'Order is being processed in workshop',
          color_class: 'bg-purple-100 text-purple-800',
          is_active: true,
          sort_order: 4
        },
        {
          id: 5,
          name: 'completed',
          display_name: 'Completed',
          description: 'Order has been completed',
          color_class: 'bg-green-100 text-green-800',
          is_active: true,
          sort_order: 5
        },
        {
          id: 6,
          name: 'delivered',
          display_name: 'Delivered',
          description: 'Order has been delivered',
          color_class: 'bg-gray-100 text-gray-800',
          is_active: true,
          sort_order: 6
        }
      ]
    } finally {
      loading.value = false
    }
  }

  const createStatus = async (statusData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await orderStatusesAPI.createStatus(statusData)
      statuses.value.push(response.status)
      return response.status
    } catch (err) {
      console.error('Failed to create order status:', err)
      error.value = err.response?.data?.message || 'Failed to create order status'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateStatus = async (statusId, statusData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await orderStatusesAPI.updateStatus(statusId, statusData)
      const index = statuses.value.findIndex(status => status.id === statusId)
      if (index !== -1) {
        statuses.value[index] = response.status
      }
      return response.status
    } catch (err) {
      console.error('Failed to update order status:', err)
      error.value = err.response?.data?.message || 'Failed to update order status'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteStatus = async (statusId) => {
    try {
      loading.value = true
      error.value = null
      
      await orderStatusesAPI.deleteStatus(statusId)
      statuses.value = statuses.value.filter(status => status.id !== statusId)
    } catch (err) {
      console.error('Failed to delete order status:', err)
      error.value = err.response?.data?.message || 'Failed to delete order status'
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    // State
    statuses,
    loading,
    error,
    
    // Getters
    activeStatuses,
    statusesMap,
    getStatusDisplayName,
    getStatusColorClass,
    getStatusDescription,
    
    // Actions
    fetchStatuses,
    createStatus,
    updateStatus,
    deleteStatus,
    clearError
  }
})
